import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import Marca from "@/components/Marca";
import { Button, Card, ErrorBanner, Input, Spinner } from "@/components/ui";
import { API_BASE, toApiError, tokens } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { InvitationInfo } from "@/lib/types";

/**
 * A tela que o colaborador abre pelo link do convite.
 *
 * Usa axios "cru" e não a instância `api`: quem chega aqui não tem
 * sessão, e o interceptor de refresh mandaria a página para /entrar no
 * primeiro 401 — matando o convite antes de ele ser aceito.
 */
export default function AcceptInvite() {
  const t = useT();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [nome, setNome] = useState("");
  const [senha, setSenha] = useState("");

  const convite = useQuery({
    queryKey: ["invitation", token],
    retry: false,
    queryFn: async () =>
      (await axios.get<InvitationInfo>(`${API_BASE}/invitations/${token}/`)).data,
  });

  const aceitar = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(`${API_BASE}/invitations/${token}/accept/`, {
        full_name: nome,
        password: senha,
      });
      return data as { needs_login: boolean; access?: string; refresh?: string };
    },
    onSuccess: (data) => {
      if (data.access && data.refresh) {
        qc.clear();
        tokens.set({ access: data.access, refresh: data.refresh });
        navigate("/", { replace: true });
      } else {
        // Conta que já existia: ela entra com a senha que já usa, e o
        // vínculo com esta oficina acabou de ser criado.
        navigate("/entrar", { replace: true });
      }
    },
  });

  if (convite.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  const dados = convite.data;
  if (convite.isError || !dados || !dados.valid) {
    return (
      <Moldura>
        <ShieldAlert className="mx-auto mb-3 text-racing-500" size={32} />
        <h1 className="text-center font-display text-xl font-bold">{t("auth.inviteUnavailable")}</h1>
        <p className="mt-2 text-center text-sm text-steel-muted">
          {dados?.status === "accepted"
            ? t("auth.inviteUsed")
            : t("auth.inviteExpired")}
        </p>
        <Link to="/entrar" className="mt-5 block">
          <Button variant="secondary" className="w-full">
            {t("auth.goToLogin")}
          </Button>
        </Link>
      </Moldura>
    );
  }

  const erro = aceitar.isError ? toApiError(aceitar.error) : null;

  return (
    <Moldura>
      <p className="eyebrow mb-1.5 text-center">{t("auth.inviteTitle")}</p>
      <h1 className="text-center font-display text-xl font-bold">{dados.garage}</h1>
      <p className="mt-2 text-center text-sm text-steel-muted">
        {dados.display_name
          ? t("auth.invitedAs", { name: dados.display_name })
          : t("auth.inviteText")}
      </p>
      <p className="mt-1 text-center text-sm text-steel-dim">{dados.email}</p>

      <form
        className="mt-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          aceitar.mutate();
        }}
      >
        {dados.existing_account ? (
          <p className="rounded-[4px] border border-line bg-carbon-900 px-4 py-3 text-sm text-steel-muted">
            {t("auth.emailExists")}
          </p>
        ) : (
          <>
            <Input
              label={t("auth.fullName")}
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              error={erro?.field === "full_name" ? erro.message : undefined}
            />
            <Input
              label={t("auth.createPassword")}
              type="password"
              autoComplete="new-password"
              required
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              hint={t("auth.minCharsPrivate")}
              error={erro?.field === "password" ? erro.message : undefined}
            />
          </>
        )}

        {erro && !erro.field && <ErrorBanner message={erro.message} />}

        <Button type="submit" className="w-full" disabled={aceitar.isPending}>
          {aceitar.isPending ? (
            <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
          ) : dados.existing_account ? (
            t("auth.acceptInvite")
          ) : (
            t("auth.creatingAccess")
          )}
        </Button>
      </form>
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-carbon-900 p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 flex justify-center">
          <Marca />
        </div>
        {children}
      </Card>
    </div>
  );
}
